// RPTLAYOUT1-F1-S3 (docs/build-briefs/RPTLAYOUT1-F1-S3-guided-report-builder.md; PRD P0.13; design/mockups/reports-experience.html
// builder scenes): the pure model of the guided custom report builder. No DOM and nothing node-only, so a plain node:test file
// imports it (test/rptlayout1-f1-s3-builder-model.test.ts) and the browser bundle does too.
//
// What it does: holds the person's choices across the five steps, turns them into the SAME version 1 or version 2 spec the older
// builder wrote (it calls buildSpec and buildComposite, so the server's validator is the only judge of what a spec may say), says
// whether each step may advance, and plans the Save. Every figure, join and total comes from the server; nothing here computes
// money. A permission is read by id, never a role name.

import type { CustomReportCatalogDto, CustomReportResultDto } from "./api.ts";
import { compositePresentation, compositeProblem, firstDataset, sourceAlias, COMMON_LABELS, type CommonKey } from "./compositeReportModel.ts";
import {
  CR_CUSTOM_DATES,
  CR_PRESETS,
  NOT_AVAILABLE,
  buildSpec,
  chartSlot,
  defaultAgg,
  dimensionFields,
  fieldNamed,
  filterOpsFor,
  formatCell,
  freshBuilder,
  isFigure,
  measureAlias,
  measureFields,
  rowCountText,
  stateFromSpec,
  totalsRow,
  valueLabelsOf,
  type BuilderState,
  type CatalogDataset,
  type FilterDraft,
  type MeasureChoice,
} from "./customReportModel.ts";
import { networkStateFromSpec, type NetworkCatalogDto } from "./franchisorCustomReportsModel.ts";
import type { ChartType } from "./reportChartModel.ts";
import type { PickerLocation, PickerValue } from "./locationPickerModel.ts";

export type FlowKind = "location" | "network";

/** The permission each console needs to build and run a report: a permission id, never a role name. */
export const BUILD_PERMISSION: Readonly<Record<FlowKind, string>> = {
  location: "report.custom.build",
  network: "network.reports.build",
};

export const FLOW_STEPS = ["Sources", "Numbers", "Filters", "Group and show", "Save"] as const;
export type StepNumber = 1 | 2 | 3 | 4 | 5;

/** The most measures one source carries (the server's bound is eight). */
export const MEASURES_PER_SOURCE = 8;

export interface FlowSource {
  dataset: string;
  measures: MeasureChoice[];
  filters: FilterDraft[];
  nextFilterId: number;
}

export type Cadence = "daily" | "weekly" | "monthly";
export interface ScheduleDraft {
  on: boolean;
  cadence: Cadence;
  /** 0 is Sunday. Only weekly reads it. */
  weekday: number;
  /** 1 to 28, or 31 for the last day. Only monthly reads it. */
  monthDay: number;
  /** HH:MM, in the location's own time. */
  time: string;
  delivery: "link" | "email";
  attachment: "csv" | "xlsx" | "pdf";
  recipientIds: string[];
}

/** What a drafted or older spec carries that the five steps do not draw. It is passed through, never dropped. */
export interface FlowExtras {
  sortBy: string;
  sortDir: "asc" | "desc";
  limit: number | null;
  totals: boolean;
  compare: BuilderState["compare"];
}

export interface FlowState {
  sources: FlowSource[];
  /** One group, or "" for a single summary row. */
  dimension: string;
  dates: string;
  from: string;
  to: string;
  scope: PickerValue;
  show: "table" | "chart";
  chart: "bar" | "line" | "pie";
  name: string;
  favorite: boolean;
  share: "me" | "named";
  people: string[];
  schedule: ScheduleDraft;
  extras: FlowExtras;
}

export const SCHEDULE_TIMES: ReadonlyArray<{ value: string; label: string }> = [
  { value: "06:00", label: "6:00 AM" },
  { value: "07:00", label: "7:00 AM" },
  { value: "08:00", label: "8:00 AM" },
  { value: "09:00", label: "9:00 AM" },
  { value: "12:00", label: "12:00 PM" },
  { value: "17:00", label: "5:00 PM" },
];
export const LAST_DAY = 31;
export const MONTH_DAYS: readonly number[] = [...Array.from({ length: 28 }, (_, at) => at + 1), LAST_DAY];
export const WEEKDAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"] as const;

export function freshSchedule(): ScheduleDraft {
  return { on: false, cadence: "weekly", weekday: 1, monthDay: 1, time: "08:00", delivery: "email", attachment: "csv", recipientIds: [] };
}

export function freshFlow(): FlowState {
  return {
    sources: [],
    dimension: "",
    dates: "last_30_days",
    from: "",
    to: "",
    scope: { mode: "all", ids: [] },
    show: "table",
    chart: "bar",
    name: "",
    favorite: false,
    share: "me",
    people: [],
    schedule: freshSchedule(),
    extras: { sortBy: "", sortDir: "desc", limit: null, totals: true, compare: "" },
  };
}

// ---------------------------------------------------------------------------------------------------------------------
// The catalog as the flow reads it
// ---------------------------------------------------------------------------------------------------------------------

export interface FlowCatalog {
  kind: FlowKind;
  datasets: CatalogDataset[];
  presets: string[];
  locations: PickerLocation[];
  businessDate: string;
}

/** The flow's view of either console's catalog. The network catalog adds the franchisee each location belongs to. */
export function flowCatalog(kind: FlowKind, dto: CustomReportCatalogDto | NetworkCatalogDto): FlowCatalog {
  const franchiseeOf = new Map<string, string>();
  const suspendedIds = new Set<string>();
  if (kind === "network" && "franchisees" in dto) {
    for (const franchisee of dto.franchisees) for (const site of franchisee.locations) {
        franchiseeOf.set(site.id, franchisee.name);
        if (franchisee.suspended === true) suspendedIds.add(site.id);
      }
  }
  return {
    kind,
    datasets: dto.catalog.datasets,
    presets: dto.presets,
    locations: dto.locations.map((site) => ({
      id: site.id,
      name: site.name,
      ...(franchiseeOf.has(site.id) ? { franchisee: franchiseeOf.get(site.id) as string } : {}),
      ...(suspendedIds.has(site.id) ? { suspended: true } : {}),
    })),
    businessDate: dto.businessDate,
  };
}

export function datasetOf(catalog: FlowCatalog, id: string): CatalogDataset | undefined {
  return catalog.datasets.find((dataset) => dataset.id === id);
}

// ---------------------------------------------------------------------------------------------------------------------
// Step 1: Sources
// ---------------------------------------------------------------------------------------------------------------------

export function hasSource(flow: FlowState, id: string): boolean {
  return flow.sources.some((source) => source.dataset === id);
}

/** A source that cannot join (no composite entry in the catalog) is offered alone only, and nothing joins to it. */
export function sourceBlocked(flow: FlowState, dataset: CatalogDataset, catalog: FlowCatalog): boolean {
  if (hasSource(flow, dataset.id) || flow.sources.length === 0) return false;
  return dataset.composite === undefined || flow.sources.some((source) => datasetOf(catalog, source.dataset)?.composite === undefined);
}

/** Whether the picked sources can be combined: a source with no composite entry is offered alone. */
export function lonelySource(flow: FlowState, catalog: FlowCatalog): CatalogDataset | undefined {
  if (flow.sources.length < 2) return undefined;
  for (const source of flow.sources) {
    const dataset = datasetOf(catalog, source.dataset);
    if (dataset?.composite === undefined) return dataset;
  }
  return undefined;
}

export function toggleSource(flow: FlowState, id: string, catalog: FlowCatalog): FlowState {
  const dataset = datasetOf(catalog, id);
  if (dataset === undefined) return flow;
  if (hasSource(flow, id)) return reconcile({ ...flow, sources: flow.sources.filter((source) => source.dataset !== id) }, catalog);
  if (sourceBlocked(flow, dataset, catalog)) return flow;
  return reconcile({ ...flow, sources: [...flow.sources, { dataset: id, measures: [], filters: [], nextFilterId: 1 }] }, catalog);
}

/** Keeps the choices that still make sense after the sources change: a group that the sources no longer share is dropped. */
function reconcile(flow: FlowState, catalog: FlowCatalog): FlowState {
  let next = flow;
  const allowed = groupChoices(next, catalog).filter((choice) => choice.enabled).map((choice) => choice.key);
  if (next.dimension !== "" && !allowed.includes(next.dimension)) next = { ...next, dimension: "", show: "table" };
  // RPTCOMPARE1: a source as of now (Stock on hand) cannot be compared with another period, so a choice made earlier goes.
  if (next.extras.compare !== "" && next.sources.length > 0 && !compareAllowed(next, catalog)) next = { ...next, extras: { ...next.extras, compare: "" } };
  return pruneSort(next);
}

/** A sort a draft brought stays while the column it names is still in the report, and goes when that column does. */
export function pruneSort(flow: FlowState): FlowState {
  const by = flow.extras.sortBy;
  if (by === "") return flow;
  const joined = flow.sources.length > 1;
  const columns = new Set<string>(flow.dimension === "" ? [] : [flow.dimension]);
  flow.sources.forEach((source, at) => {
    for (const measure of source.measures) columns.add(joined ? `s${at + 1}_${sourceAlias(measure)}` : measureAlias(measure));
  });
  return columns.has(by) ? flow : { ...flow, extras: { ...flow.extras, sortBy: "" } };
}

/** "Tickets and Payments are joined by Location and Business date." Empty for fewer than two sources. */
export function joinSentence(flow: FlowState, catalog: FlowCatalog): string {
  if (flow.sources.length < 2) return "";
  const datasets = flow.sources.map((source) => datasetOf(catalog, source.dataset));
  if (datasets.some((dataset) => dataset?.composite === undefined)) return "";
  const names = datasets.map((dataset) => (dataset as CatalogDataset).label);
  const common = commonKeys(datasets as CatalogDataset[]);
  const joined = common.filter((key) => key === "location_id" || key === "business_date");
  const parts = (joined.length > 0 ? joined : common.slice(0, 2)).map((key) => labelOfKey(key, datasets as CatalogDataset[]));
  if (parts.length === 0) return "";
  return `${listText(names)} are joined by ${listText(parts)}.`;
}

function listText(items: readonly string[]): string {
  if (items.length <= 1) return items[0] ?? "";
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1] as string}`;
}

function commonKeys(datasets: readonly CatalogDataset[]): string[] {
  const first = datasets[0]?.composite?.dimensions.map((dimension) => dimension.key) ?? [];
  return first.filter((key) => datasets.every((dataset) => dataset.composite?.dimensions.some((dimension) => dimension.key === key) === true));
}

function labelOfKey(key: string, datasets: readonly CatalogDataset[]): string {
  const own = datasets[0]?.composite?.dimensions.find((dimension) => dimension.key === key)?.label;
  return own ?? COMMON_LABELS[key as CommonKey] ?? key;
}

/**
 * REPORTUX1 (baseline 11): the summary that sits beside the live preview, so a person sees what the rows are made of without going
 * back a step: the sources, the period and the location scope. Each line is read from the wizard's own state, nothing is invented.
 */
export function previewSummary(flow: FlowState, catalog: FlowCatalog): Array<{ label: string; value: string }> {
  const lines: Array<{ label: string; value: string }> = [];
  const sources = flow.sources.map((source) => datasetOf(catalog, source.dataset)?.label).filter((label): label is string => label !== undefined);
  if (sources.length > 0) lines.push({ label: sources.length === 1 ? "Source" : "Sources", value: listText(sources) });
  if (flow.sources.length > 0) {
    if (!datesApply(flow, catalog)) lines.push({ label: "Period", value: "As of now" });
    else if (flow.dates === CR_CUSTOM_DATES) lines.push({ label: "Period", value: flow.from !== "" && flow.to !== "" ? `${flow.from} to ${flow.to}` : "Choose the dates" });
    else lines.push({ label: "Period", value: CR_PRESETS.find((preset) => preset.id === flow.dates)?.label ?? flow.dates });
  }
  const total = catalog.locations.length;
  if (total > 0) {
    const one = flow.scope.mode !== "all" && flow.scope.ids.length === 1 ? catalog.locations.find((site) => site.id === flow.scope.ids[0])?.name : undefined;
    const value = total === 1 ? (catalog.locations[0]?.name ?? "1 location") : flow.scope.mode === "all" ? `All ${total} locations` : (one ?? `${flow.scope.ids.length} locations`);
    lines.push({ label: "Locations", value });
  }
  return lines;
}

/** The sources that match a search, case blind. An empty search keeps every source. */
export function searchSources(datasets: readonly CatalogDataset[], query: string): CatalogDataset[] {
  const needle = query.trim().toLowerCase();
  return needle === "" ? [...datasets] : datasets.filter((dataset) => dataset.label.toLowerCase().includes(needle));
}

// ---------------------------------------------------------------------------------------------------------------------
// Step 2: Numbers
// ---------------------------------------------------------------------------------------------------------------------

export interface NumberChoice {
  field: string;
  label: string;
  alias: string;
  on: boolean;
}

export function numberChoices(source: FlowSource, catalog: FlowCatalog): NumberChoice[] {
  const dataset = datasetOf(catalog, source.dataset);
  if (dataset === undefined) return [];
  return measureFields(dataset).map((field) => {
    const measure = { field: field.name, agg: defaultAgg(field) };
    return {
      field: field.name,
      label: field.label,
      alias: measureAlias(measure),
      on: source.measures.some((chosen) => chosen.field === field.name),
    };
  });
}

export function toggleNumber(flow: FlowState, datasetId: string, field: string, catalog: FlowCatalog): FlowState {
  const dataset = datasetOf(catalog, datasetId);
  const fieldDto = dataset === undefined ? undefined : fieldNamed(dataset, field);
  if (fieldDto === undefined || fieldDto.kind !== "measure") return flow;
  const sources = flow.sources.map((source) => {
    if (source.dataset !== datasetId) return source;
    if (source.measures.some((measure) => measure.field === field)) return { ...source, measures: source.measures.filter((measure) => measure.field !== field) };
    if (source.measures.length >= MEASURES_PER_SOURCE) return source;
    return { ...source, measures: [...source.measures, { field, agg: defaultAgg(fieldDto) }] };
  });
  return pruneSort({ ...flow, sources });
}

// ---------------------------------------------------------------------------------------------------------------------
// Step 3: Filters
// ---------------------------------------------------------------------------------------------------------------------

/** The dates shown as one tap. A preset the older builder or a draft chose that is not in this list is shown as well, on. */
export const QUICK_PRESETS: readonly string[] = ["last_7_days", "last_30_days", "last_month", "last_quarter", "year_to_date", "last_year"];

export function presetChips(catalog: FlowCatalog, current: string): Array<{ id: string; label: string }> {
  const offered = (id: string) => catalog.presets.includes(id) && CR_PRESETS.some((preset) => preset.id === id);
  const ids = QUICK_PRESETS.filter(offered);
  if (current !== CR_CUSTOM_DATES && !ids.includes(current) && CR_PRESETS.some((preset) => preset.id === current)) ids.push(current);
  return ids.map((id) => ({ id, label: CR_PRESETS.find((preset) => preset.id === id)?.label ?? id }));
}

/** Dates apply when any picked source is dated; a snapshot (Stock on hand) has no dates. */
export function datesApply(flow: FlowState, catalog: FlowCatalog): boolean {
  return flow.sources.some((source) => datasetOf(catalog, source.dataset)?.dated !== false);
}

/**
 * RPTCOMPARE1 (owner ruling 2026-10-20, DEC-485): the optional Compare choice. The words are the owner's: none, previous period,
 * same period last year. The values are the spec's own `compare.kind`, the same field an AI drafted report carries.
 */
export const COMPARE_OPTIONS: ReadonlyArray<{ value: BuilderState["compare"]; label: string }> = [
  { value: "", label: "None" },
  { value: "prior_period", label: "Previous period" },
  { value: "same_period_last_year", label: "Same period last year" },
];

/** Compare needs dates in every source: the server refuses it on a report that is as of now. */
export function compareAllowed(flow: FlowState, catalog: FlowCatalog): boolean {
  return flow.sources.length > 0 && flow.sources.every((source) => datasetOf(catalog, source.dataset)?.dated !== false);
}

export function setCompare(flow: FlowState, compare: BuilderState["compare"], catalog: FlowCatalog): FlowState {
  if (compare !== "" && !compareAllowed(flow, catalog)) return flow;
  return { ...flow, extras: { ...flow.extras, compare } };
}

export interface FilterField {
  source: string;
  field: string;
  label: string;
}

/** The fields a filter may name. Groups (dimensions) only: a test on a total is a measure filter the older builder keeps. */
export function filterFieldChoices(flow: FlowState, catalog: FlowCatalog): FilterField[] {
  return flow.sources.flatMap((source) => {
    const dataset = datasetOf(catalog, source.dataset);
    if (dataset === undefined) return [];
    return dimensionFields(dataset).map((field) => ({
      source: source.dataset,
      field: field.name,
      label: flow.sources.length > 1 ? `${dataset.label}, ${field.label}` : field.label,
    }));
  });
}

export function addFlowFilter(flow: FlowState, datasetId: string, field: string, catalog: FlowCatalog): FlowState {
  const dataset = datasetOf(catalog, datasetId);
  const fieldDto = dataset === undefined ? undefined : fieldNamed(dataset, field);
  if (fieldDto === undefined) return flow;
  return {
    ...flow,
    sources: flow.sources.map((source) => {
      if (source.dataset !== datasetId || source.filters.length >= 10) return source;
      const op = fieldDto.type === "text" ? "contains" : (filterOpsFor(fieldDto)[0]?.op ?? "eq");
      const value = fieldDto.type === "boolean" ? "true" : "";
      return { ...source, filters: [...source.filters, { id: source.nextFilterId, field, op, value, value2: "" }], nextFilterId: source.nextFilterId + 1 };
    }),
  };
}

export function updateFlowFilter(flow: FlowState, datasetId: string, id: number, change: Partial<Omit<FilterDraft, "id" | "field">>): FlowState {
  return { ...flow, sources: flow.sources.map((source) => (source.dataset !== datasetId ? source : { ...source, filters: source.filters.map((filter) => (filter.id === id ? { ...filter, ...change } : filter)) })) };
}

export function removeFlowFilter(flow: FlowState, datasetId: string, id: number): FlowState {
  return { ...flow, sources: flow.sources.map((source) => (source.dataset !== datasetId ? source : { ...source, filters: source.filters.filter((filter) => filter.id !== id) })) };
}

/** A filter with no value yet. It is held back from the preview and the run, and Next waits for it. */
export function filterIncomplete(filter: FilterDraft): boolean {
  return filter.value.trim() === "" || (filter.op === "between" && filter.value2.trim() === "");
}

export function hasIncompleteFilter(flow: FlowState): boolean {
  return flow.sources.some((source) => source.filters.some(filterIncomplete));
}

export function setScope(flow: FlowState, scope: PickerValue): FlowState {
  return { ...flow, scope };
}

export function setPreset(flow: FlowState, preset: string): FlowState {
  return { ...flow, dates: preset, from: "", to: "" };
}

export function setCustomDates(flow: FlowState, from: string, to: string): FlowState {
  return { ...flow, dates: CR_CUSTOM_DATES, from, to };
}

// ---------------------------------------------------------------------------------------------------------------------
// Step 4: Group and show
// ---------------------------------------------------------------------------------------------------------------------

export interface GroupChoice {
  key: string;
  label: string;
  enabled: boolean;
}

export function groupChoices(flow: FlowState, catalog: FlowCatalog): GroupChoice[] {
  const datasets = flow.sources.map((source) => datasetOf(catalog, source.dataset));
  if (datasets.length === 0 || datasets.some((dataset) => dataset === undefined)) return [];
  const known = datasets as CatalogDataset[];
  if (known.length === 1) return dimensionFields(known[0] as CatalogDataset).map((field) => ({ key: field.name, label: field.label, enabled: true }));
  if (known.some((dataset) => dataset.composite === undefined)) return [];
  return commonKeys(known).map((key) => ({
    key,
    label: labelOfKey(key, known),
    enabled: compositeProblem(composeBuilder({ ...flow, dimension: key, show: "table" }, catalog), catalog.datasets) === "",
  }));
}

export function setDimension(flow: FlowState, key: string): FlowState {
  const next = pruneSort({ ...flow, dimension: flow.dimension === key ? "" : key });
  return next.dimension === "" ? { ...next, show: "table" } : next;
}

export const CHART_CHOICES: ReadonlyArray<{ id: FlowState["chart"]; label: string }> = [
  { id: "bar", label: "Bar" },
  { id: "line", label: "Lines" },
  { id: "pie", label: "Pie" },
];

/** Whether a chart can be drawn for the choices: the same rules the server's validator applies (chartSlot). */
export function chartAllowed(flow: FlowState, catalog: FlowCatalog, type: FlowState["chart"] = flow.chart): boolean {
  if (flow.dimension === "") return false;
  const first = flow.sources[0];
  if (first === undefined || flow.sources.some((source) => source.measures.length === 0)) return false;
  // A joined report charts its first number, so any of the three charts can be drawn once there is a group.
  if (flow.sources.length > 1) return true;
  const dataset = datasetOf(catalog, first.dataset);
  if (dataset === undefined) return false;
  const base = composeBuilder({ ...flow, show: "table" }, catalog);
  return chartSlot(base, dataset, type).ok;
}

export function setShow(flow: FlowState, show: FlowState["show"], catalog: FlowCatalog): FlowState {
  if (show === "chart" && !chartAllowed(flow, catalog)) return flow;
  return { ...flow, show };
}

export function setChartType(flow: FlowState, type: FlowState["chart"], catalog: FlowCatalog): FlowState {
  return chartAllowed(flow, catalog, type) ? { ...flow, chart: type } : flow;
}

// ---------------------------------------------------------------------------------------------------------------------
// The spec
// ---------------------------------------------------------------------------------------------------------------------

/** The older builder's state for these choices, so buildSpec and buildComposite do the building. Incomplete filters are held back. */
export function composeBuilder(flow: FlowState, catalog: FlowCatalog): BuilderState {
  const first = flow.sources[0];
  const base = freshBuilder(first?.dataset ?? catalog.datasets[0]?.id ?? "");
  base.title = flow.name;
  base.dates = flow.dates;
  base.from = flow.from;
  base.to = flow.to;
  base.dimensions = flow.dimension === "" ? [] : [flow.dimension];
  base.sortBy = flow.extras.sortBy;
  base.sortDir = flow.extras.sortDir;
  base.limit = flow.extras.limit;
  base.compare = flow.extras.compare;
  if (!flow.extras.totals) base.totals = false;
  const complete = (source: FlowSource) => source.filters.filter((filter) => !filterIncomplete(filter));
  if (flow.sources.length <= 1) {
    if (first !== undefined) {
      base.measures = first.measures;
      base.filters = complete(first);
      base.nextFilterId = first.nextFilterId;
    }
    if (flow.show === "chart") base.chart = flow.chart;
    return base;
  }
  const sources = flow.sources.map((source, at) => ({ id: `s${at + 1}`, dataset: source.dataset, measures: source.measures, filters: complete(source), nextFilterId: source.nextFilterId }));
  const lead = sources[0]?.measures[0];
  base.composite = {
    sources,
    calculations: [],
    ...(flow.show === "chart" && flow.dimension !== "" && lead !== undefined
      ? { chart: { type: flow.chart, x: flow.dimension, y: [`s1_${sourceAlias(lead)}`], series: null } }
      : {}),
  };
  return base;
}

export function locationsOf(flow: FlowState): Record<string, unknown> | undefined {
  if (flow.scope.mode === "all") return { kind: "all_permitted" };
  return flow.scope.ids.length === 0 ? undefined : { kind: "list", ids: [...flow.scope.ids] };
}

export type FlowSpec = { ok: true; spec: Record<string, unknown> } | { ok: false; message: string };

/** The spec for the choices, or one sentence saying what is missing. The server validates it again. */
export function flowSpec(flow: FlowState, catalog: FlowCatalog): FlowSpec {
  const first = flow.sources[0];
  if (first === undefined) return { ok: false, message: "Pick a source." };
  const dataset = datasetOf(catalog, first.dataset);
  if (dataset === undefined) return { ok: false, message: "A source is not available." };
  const locations = locationsOf(flow);
  if (locations === undefined) return { ok: false, message: "Pick at least one location." };
  const built = buildSpec(composeBuilder(flow, catalog), dataset, catalog.datasets);
  if (!built.ok) return built;
  return { ok: true, spec: { ...built.spec, locations } };
}

/** The spec the preview runs: only once every source has a number, so a half-built report never reaches the server. */
export function previewSpec(flow: FlowState, catalog: FlowCatalog): FlowSpec {
  if (flow.sources.length === 0 || flow.sources.some((source) => source.measures.length === 0)) return { ok: false, message: "Pick a number." };
  return flowSpec(flow, catalog);
}

// ---------------------------------------------------------------------------------------------------------------------
// Steps
// ---------------------------------------------------------------------------------------------------------------------

/** Whether the person may leave `step` going forward. */
export function stepReady(flow: FlowState, step: StepNumber, catalog: FlowCatalog): boolean {
  switch (step) {
    case 1:
      return flow.sources.length > 0 && lonelySource(flow, catalog) === undefined;
    case 2:
      return flow.sources.length > 0 && flow.sources.every((source) => source.measures.length > 0);
    case 3:
      return !hasIncompleteFilter(flow) && (flow.dates !== CR_CUSTOM_DATES || (flow.from !== "" && flow.to !== "" && flow.from <= flow.to)) && locationsOf(flow) !== undefined;
    case 4:
      return flowSpec(flow, catalog).ok;
    case 5:
      return saveProblem(flow, catalog) === "";
  }
}

/** The furthest step a person may jump to from the stepper: every earlier step must be ready. */
export function furthestStep(flow: FlowState, catalog: FlowCatalog): StepNumber {
  let step: StepNumber = 1;
  while (step < 5 && stepReady(flow, step, catalog)) step = (step + 1) as StepNumber;
  return step;
}

export function nextStep(step: StepNumber): StepNumber {
  return step === 5 ? 5 : ((step + 1) as StepNumber);
}
export function previousStep(step: StepNumber): StepNumber | 0 {
  return step === 1 ? 0 : ((step - 1) as StepNumber);
}

/** Step 1 is the only one drawn without the preview, and the preview starts at step 2 (design). */
export function previewShows(step: StepNumber): boolean {
  return step >= 2;
}

// ---------------------------------------------------------------------------------------------------------------------
// Step 5: Save
// ---------------------------------------------------------------------------------------------------------------------

export function nameProblem(name: string): string {
  const trimmed = name.trim();
  if (trimmed === "") return "Name the report.";
  return trimmed.length > 120 ? "Use 120 characters or fewer." : "";
}

export function scheduleProblem(schedule: ScheduleDraft): string {
  if (!schedule.on) return "";
  if (schedule.recipientIds.length === 0) return "Choose a recipient.";
  if (!/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(schedule.time)) return "Choose a time.";
  return "";
}

/** One sentence for whatever stops Save, or empty when Save may proceed. */
export function saveProblem(flow: FlowState, catalog: FlowCatalog): string {
  const named = nameProblem(flow.name);
  if (named !== "") return named;
  const spec = flowSpec(flow, catalog);
  if (!spec.ok) return spec.message;
  if (flow.share === "named" && flow.people.length === 0) return "Choose who to share with.";
  return scheduleProblem(flow.schedule);
}

export type SaveStepId = "create" | "audience" | "schedule" | "favorite";

/** What Save does, in order. A later step that fails leaves the saved report in place and can be retried alone. */
export function savePlan(flow: FlowState, can: { share: boolean; schedule: boolean }): SaveStepId[] {
  const plan: SaveStepId[] = ["create"];
  if (flow.share === "named" && can.share) plan.push("audience");
  if (flow.schedule.on && can.schedule) plan.push("schedule");
  if (flow.favorite) plan.push("favorite");
  return plan;
}

/** The body of POST schedules/save for a saved version. Weekday only for weekly, day of month only for monthly. */
export function scheduleBody(schedule: ScheduleDraft, definitionId: string, version: number): Record<string, unknown> {
  return {
    definitionId,
    version,
    cadence: schedule.cadence,
    time: schedule.time,
    ...(schedule.cadence === "weekly" ? { weekday: schedule.weekday } : {}),
    ...(schedule.cadence === "monthly" ? { monthDay: schedule.monthDay } : {}),
    recipientIds: [...schedule.recipientIds],
    attachmentFormat: schedule.delivery === "email" ? schedule.attachment : "link",
  };
}

export function timeLabel(time: string): string {
  return SCHEDULE_TIMES.find((choice) => choice.value === time)?.label ?? time;
}

export function monthDayLabel(day: number): string {
  return day === LAST_DAY ? "Last day" : String(day);
}

// ---------------------------------------------------------------------------------------------------------------------
// From a drafted or saved spec back to the flow
// ---------------------------------------------------------------------------------------------------------------------

function isObj(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * The flow's choices for a spec an AI drafted or a person saved earlier. Undefined when the five steps cannot hold it exactly
 * (a calculation, a tile, a stacked or number chart, two groups, a named list of several locations in the location console):
 * the screen then says so rather than quietly changing what the report does.
 */
export function flowFromSpec(raw: unknown, catalog: FlowCatalog, tree?: NetworkCatalogDto["franchisees"]): FlowState | undefined {
  const dataset = firstDataset(raw, catalog.datasets);
  if (dataset === undefined || !isObj(raw)) return undefined;
  let builder: BuilderState | undefined;
  let scope: PickerValue = { mode: "all", ids: [] };
  if (catalog.kind === "network" && tree !== undefined) {
    const held = networkStateFromSpec(raw, dataset, tree, catalog.datasets);
    if (held === undefined) return undefined;
    builder = held.state;
    const all = tree.flatMap((franchisee) => franchisee.locations.map((site) => site.id));
    const picked = [...held.selection];
    scope = picked.length === all.length ? { mode: "all", ids: [] } : { mode: picked.length === 1 ? "one" : "selected", ids: picked };
  } else {
    builder = stateFromSpec(raw, dataset, catalog.datasets);
    if (builder === undefined) return undefined;
    if (builder.location !== "all") scope = { mode: "one", ids: [builder.location] };
  }
  if (builder.tile !== undefined || builder.dimensions.length > 1) return undefined;
  const flow = freshFlow();
  flow.name = builder.title;
  flow.dates = builder.dates;
  flow.from = builder.from;
  flow.to = builder.to;
  flow.scope = scope;
  flow.dimension = builder.dimensions[0] ?? "";
  flow.extras = { sortBy: builder.sortBy, sortDir: builder.sortDir, limit: builder.limit, totals: builder.totals !== false, compare: builder.compare };
  if (builder.composite !== undefined) {
    if (builder.composite.calculations.length > 0) return undefined;
    flow.sources = builder.composite.sources.map((source) => ({ dataset: source.dataset, measures: source.measures.map(({ field, agg }) => ({ field, agg })), filters: source.filters, nextFilterId: source.nextFilterId }));
    const chart = builder.composite.chart;
    if (chart !== undefined) {
      const type = chart["type"];
      if (type !== "bar" && type !== "line" && type !== "pie") return undefined;
      flow.show = "chart";
      flow.chart = type;
    }
  } else {
    flow.sources = [{ dataset: builder.dataset, measures: builder.measures, filters: builder.filters, nextFilterId: builder.nextFilterId }];
    if (builder.chart !== null) {
      if (builder.chart !== "bar" && builder.chart !== "line" && builder.chart !== "pie") return undefined;
      flow.show = "chart";
      flow.chart = builder.chart as ChartType & FlowState["chart"];
    }
  }
  // The flow must reproduce the very spec it was given, or it would run something else.
  const rebuilt = flowSpec(flow, catalog);
  if (!rebuilt.ok) return undefined;
  return flow;
}

// ---------------------------------------------------------------------------------------------------------------------
// The preview
// ---------------------------------------------------------------------------------------------------------------------

export interface PreviewTable {
  heads: Array<{ label: string; figure: boolean }>;
  rows: string[][];
  total: string[] | null;
  count: string;
  /** Rows beyond the ones drawn. */
  more: number;
}

/** The first rows of a run, as the screen draws them. Cells are the server's, formatted for reading. */
export function previewTable(result: CustomReportResultDto, catalog: FlowCatalog, limit = 6): PreviewTable {
  const shown = compositePresentation(result);
  const labels = valueLabelsOf(datasetOf(catalog, result.meta.dataset));
  const rows = shown.rows.slice(0, limit).map((row) =>
    row.map((cell, at) => {
      const column = shown.columns[at];
      return column === undefined ? NOT_AVAILABLE : formatCell(cell, column.type, labels[column.field]?.[String(cell)] === undefined ? undefined : labels[column.field]);
    }),
  );
  return {
    heads: shown.columns.map((column) => ({ label: column.label, figure: isFigure(column) })),
    rows,
    total: totalsRow(shown),
    count: rowCountText(shown),
    more: Math.max(0, shown.rows.length - rows.length),
  };
}

export type PreviewStatus = "idle" | "loading" | "ok" | "empty" | "busy" | "denied" | "error";

/** The preview's state from a finished run. 409 is the one-report-running rule (DEC-435). */
export function previewStatusOf(reply: { status: number; body: unknown }): PreviewStatus {
  if (reply.status === 409) return "busy";
  if (reply.status === 401 || reply.status === 403) return "denied";
  if (reply.status !== 200) return "error";
  const rows = isObj(reply.body) ? reply.body["rows"] : undefined;
  return Array.isArray(rows) ? (rows.length === 0 ? "empty" : "ok") : "error";
}

/** A run is reasked only when the spec changed; the same spec twice is one request. */
export function sameSpec(left: unknown, right: unknown): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

// ---------------------------------------------------------------------------------------------------------------------
// Describe it (AI)
// ---------------------------------------------------------------------------------------------------------------------

export interface AiStatus {
  available: boolean;
  enabled: boolean;
  acknowledged: boolean;
  termsVersion: string | null;
  rateLine: string | null;
}

export function isAiStatus(value: unknown): value is AiStatus {
  return isObj(value) && typeof value["available"] === "boolean" && typeof value["acknowledged"] === "boolean" && typeof value["enabled"] === "boolean";
}

/** The Describe it choice is drawn only when the server says AI help is available to this person. */
export function aiOffered(status: AiStatus | undefined): boolean {
  return status !== undefined && status.available && status.enabled;
}

/** The first use asks for the person's own acknowledgement before any draft (DEC-432). */
export function aiNeedsAcknowledgement(status: AiStatus): boolean {
  return !status.acknowledged;
}

export const AI_IDEAS: readonly string[] = [
  "Net sales by category last month",
  "Tips by employee last week",
  "Vendor spend by quarter",
  "Voids by location",
];

/** The person's own description is trimmed and bounded the way the server bounds it (2000 characters). */
export function aiDescription(text: string): string {
  return text.trim().slice(0, 2000);
}
